// /_pdf-intake — headless only. Turns a customer's PDF into photos.
//
// Driven by worker/src/lib/pdfIntake.ts through Browser Rendering, exactly
// like /_render-spike. The PDF was stored whole by the relay; this page pulls
// it, rasterises every page with the same pdfjs the admin import and the 3D
// preview use, and posts each page back to the worker, which files it as a
// photo the customer sent. Reports through `window.__storizPdfIntake`, which
// the worker polls (never waitForFunction — the 180 s protocol ceiling).
//
// ⚠️ ONE PAGE AT A TIME, IN ORDER. Rasterising all pages first would hold a
// 77 MB PDF's worth of JPEGs in memory; posting as we go keeps the page light
// and lets a crash mid-way leave the first N pages already filed (idempotent
// upsert on the worker side, so a re-run repairs rather than duplicates).
//
// Gated by a server-only probe token. Not linked from any UI.
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { forEachPdfPage, pdfPageCount } from '@/lib/pdfRaster';

const PROBE = 'sz-pdf-7c1e4b2a9d3f';
const API_BASE = import.meta.env.VITE_API_URL ?? '/api';
/** Same long edge the upload picker keeps for print (server-side rework). */
const LONG_EDGE_PX = 3600;

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let s = '';
  for (let i = 0; i < buf.length; i += 0x8000) s += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(s);
}

export default function PdfIntakeProbe() {
  const [sp] = useSearchParams();
  const ran = useRef(false);
  const [msg, setMsg] = useState('starting…');

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const W = window as any;
    W.__storizPdfIntake = { done: false };
    const media = sp.get('media');
    const probe = sp.get('probe');
    const finish = (r: { ok: boolean; pages?: number; error?: string }) => {
      W.__storizPdfIntake = { done: true, ...r };
      setMsg(r.ok ? `done: ${r.pages} page(s)` : `failed: ${r.error}`);
    };

    (async () => {
      if (probe !== PROBE || !media) return finish({ ok: false, error: 'bad probe / media' });
      const q = `?probe=${encodeURIComponent(probe)}`;
      const report = (error: string) =>
        fetch(`${API_BASE}/whatsapp/intake/${encodeURIComponent(media)}/done${q}`, {
          method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ error }),
        }).catch(() => {});
      try {
        const man = await fetch(`${API_BASE}/whatsapp/intake/${encodeURIComponent(media)}${q}`);
        if (!man.ok) return finish({ ok: false, error: `manifest ${man.status}` });
        const { pdf_url } = await man.json() as { pdf_url: string };
        setMsg('downloading pdf…');
        const res = await fetch(pdf_url);
        if (!res.ok) { await report(`pdf fetch ${res.status}`); return finish({ ok: false, error: `pdf fetch ${res.status}` }); }
        const bytes = new Uint8Array(await res.arrayBuffer());
        const total = await pdfPageCount(bytes);
        if (!(total >= 1)) { await report('no pages'); return finish({ ok: false, error: 'no pages' }); }
        // ⚠️ A CAP, SAID OUT LOUD. 300 pages is the album ceiling; a 900-page
        // scan is not an album and would hold the browser for an hour.
        if (total > 300) { await report(`too many pages: ${total}`); return finish({ ok: false, error: `too many pages: ${total}` }); }

        await forEachPdfPage(bytes, { longEdgePx: LONG_EDGE_PX, quality: 0.9 }, async (pg, i) => {
          setMsg(`page ${i + 1}/${total}`);
          const post = await fetch(`${API_BASE}/whatsapp/intake/${encodeURIComponent(media)}/page${q}`, {
            method: 'POST', headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              page: i + 1, pages: total, width: pg.width, height: pg.height,
              jpeg_base64: await blobToBase64(pg.blob),
            }),
          });
          if (!post.ok) throw new Error(`page ${i + 1} refused: ${post.status} ${(await post.text()).slice(0, 120)}`);
          console.log(`[pdf-intake] page ${i + 1}/${total} filed`);
        });
        const done = await fetch(`${API_BASE}/whatsapp/intake/${encodeURIComponent(media)}/done${q}`, {
          method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ pages: total }),
        });
        if (!done.ok) throw new Error(`done refused: ${done.status}`);
        finish({ ok: true, pages: total });
      } catch (e) {
        const error = (e as Error).message.slice(0, 300);
        await report(error);
        finish({ ok: false, error });
      }
    })();
  }, [sp]);

  return <pre style={{ padding: 16, fontFamily: 'monospace' }}>pdf-intake: {msg}</pre>;
}
